import React, { useRef, useState, useEffect } from 'react'
import SuperUserAccountPageUI from '../../../templates/SuperUserAccountPageUI.js'
import SpinnerComponentUI from '../../../components/common/SpinnerComponentUI'
import GenericErrorUI from '../../../components/common/GenericErrorUI'
import IconButtonUI from '../../../widgets/action/IconButtonUI'
import './WhatsAppChat.css'
import EmptyListUI from '../../../widgets/output/EmptyListUI.js'

const WhatsAppChatPageUI = ({ 
  context, 
  account, 
  loading, 
  errorList, 
  messages, 
  conversations = [], 
  selectedUserIndex = 0, 
  onUserSelect,
  onFileSelect,
  onMessageChange,
  onSendMessage,
  messageText = '',
  isUploading = false,
  uploadError = null,
  selectedFile = null,
  fetchMediaContent
}) => {

  const fileInputRef = useRef(null)
  const messagesContainerRef = useRef(null)
  // State to track media content URLs
  const [mediaUrls, setMediaUrls] = useState({})
  const [loadingMedia, setLoadingMedia] = useState({})
  const [mediaErrors, setMediaErrors] = useState({})

  // More robust scrolling function that ensures we scroll to bottom
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };

  // Scroll to bottom when messages change, when media loads, or when user switches conversations
  useEffect(() => {
    // Scroll immediately for quick response
    scrollToBottom();
    
    // Also delay to ensure DOM fully updates, especially for images
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 200);
    
    // Set a second delayed timer for media that might take longer to load
    const secondTimer = setTimeout(() => {
      scrollToBottom();
    }, 500);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(secondTimer);
    };
  }, [messages, mediaUrls, selectedUserIndex]);
  
  // Also scroll when the component first mounts
  useEffect(() => {
    scrollToBottom();
  }, []);

  useEffect(() => {
    if (!messages || !messages.length) return;
    
    // Clear previous media URLs when messages change
    setMediaUrls({});
    setLoadingMedia({});
    setMediaErrors({});
    
    // Find messages with media content
    const mediaMessages = messages.filter(msg => msg.mediaPath);
    
    if (mediaMessages.length === 0) return;
    
    // Create a new object for initial loading state
    const initialLoadingState = {};
    mediaMessages.forEach(msg => {
      initialLoadingState[msg.trackerId] = true;
    });
    
    // Set loading state for all media messages at once
    setLoadingMedia(initialLoadingState);
    
    // Fetch each media URL with a delay between requests to avoid race conditions
    mediaMessages.forEach((msg, index) => {
      // Add a small delay between requests to avoid race conditions
      setTimeout(() => {        
        fetchMediaContent(msg.mediaPath, msg.trackerId, (signedUrl, path, trackerId) => {
          
          if (signedUrl) {
            setMediaUrls(prev => {
              const updated = { ...prev, [trackerId]: signedUrl };
              return updated;
            });
            
            setMediaErrors(prev => ({
              ...prev,
              [trackerId]: null
            }));
          } else {
            setMediaErrors(prev => ({
              ...prev,
              [trackerId]: 'Failed to load media'
            }));
          }
          
          setLoadingMedia(prev => ({
            ...prev,
            [trackerId]: false
          }));
        });
      }, index * 100); // Stagger requests by 100ms
    });
  }, [messages, fetchMediaContent]);

  const onAttachmentClick = () => {
    if (fileInputRef.current) fileInputRef.current.click()
  }

  // Helper to determine file type icon
  const getFileTypeIcon = (type) => {
    switch(type) {
      case 'document': return 'icon-file-text'
      case 'audio': return 'icon-volume-up'
      case 'video': return 'icon-film'
      default: return 'icon-paperclip'
    }
  }
  
  // Helper to render appropriate media content
  const renderMediaContent = (message, sender) => {
    const { trackerId, type, mediaPath } = message
    const isLoading = loadingMedia[trackerId]
    const error = mediaErrors[trackerId]
    const url = mediaUrls[trackerId]
    
    if (isLoading) {
      return (
        <div className="media-loading">
          <div className="loading-spinner"></div>
          <div>Loading media...</div>
        </div>
      )
    }
    
    if (error) {
      return (
        <div className="media-error">
          <IconButtonUI icon="icon-exclamation-triangle" />
          <div>{error}</div>
        </div>
      )
    }
    
    if (!url) return null
    
    switch(type) {
      case 'image':
        return (
          <div className={`message-image ${sender}`}>
            <img className="message-img" src={url} alt="Shared image" />
            <div className="download-icon">
              <a href={url} download target="_blank" rel="noopener noreferrer">
                <IconButtonUI icon="icon-download" />
              </a>
            </div>
          </div>
        )
        
      case 'video':
        return (
          <div className={`message-video ${sender}`}>
            <video controls className="message-video-player">
              <source src={url} type="video/mp4" />
              Your browser does not support video playback.
            </video>
            <div className="download-icon">
              <a href={url} download target="_blank" rel="noopener noreferrer">
                <IconButtonUI icon="icon-download" />
              </a>
            </div>
          </div>
        )
        
      case 'audio':
        return (
          <div className={`message-audio ${sender}`}>
            <audio controls className="message-audio-player">
              <source src={url} />
              Your browser does not support audio playback.
            </audio>
            <div className="download-icon">
              <a href={url} download target="_blank" rel="noopener noreferrer">
                <IconButtonUI icon="icon-download" />
              </a>
            </div>
          </div>
        )
        
      case 'document':
      case 'file':
      default:
        // Extract filename from path
        const filename = mediaPath.split('/').pop()
        return (
          <div className={`message-file ${sender}`}>
            {/* <div className="file-icon">
              <IconButtonUI icon={getFileTypeIcon(type)} />
            </div> */}
            <div className="file-name">{filename}</div>
            <div className="download-icon">
              <a href={url} download target="_blank" rel="noopener noreferrer">
                <IconButtonUI icon="icon-download" />
              </a>
            </div>
          </div>
        )
    }
  }

  return (
    <SuperUserAccountPageUI
      context={context}
      className='pg_su_whatsapp_chat'
      backLink='/su/accounts'
      headerText={`${account ? account.accountName : ''}`}
      centered={loading}
      account={account}
      page='whatsappChat'
    >
      {loading && <SpinnerComponentUI />}
      
      {errorList && errorList.length > 0 && (
        <GenericErrorUI error={errorList[0]} />
      )}

      {!loading && account && (
        <div className="chat-container">
          {conversations.length === 0 ? (
            <EmptyListUI
              context={context}
              className='g_m_top_100'
              createDisabledMessageLabel='No WhatsApp conversations available'
            />
          ) : (
            <div className="chat-interface">
              <div className="chat-content">
                <div className="chat-header">
                  <div className="header-left">
                    <div className="header-title">WhatsApp Message</div>
                  </div>
                  {/* <div className="header-right">
                    <a href="#" className="company-link">{account.accountName}</a>
                  </div> */}
                </div>

                <div className="users-list horizontal">
                  {conversations.map((conversation, index) => (
                    <div 
                      key={`${conversation.whatsAppConversationsPhoneNumber}-${conversation.userName}`}
                      className={`user-item ${selectedUserIndex === index ? 'selected' : ''}`}
                      onClick={() => onUserSelect(index)}
                    >
                      <div className="user-avatar">
                        {conversation.userName.charAt(0).toUpperCase()}
                      </div>
                      <div className="user-info">
                        <div className="user-name">{conversation.userName}</div>
                        <div className="user-phone">{conversation.whatsAppConversationsPhoneNumber}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="chat-messages" ref={messagesContainerRef}>
                  {messages && messages.map(message => (
                    <div key={message.trackerId} className={`message-wrapper ${message.sender}`}>
                      {message.mediaPath && message.text ? (
                        <React.Fragment>
                          <div className={`message-media ${message.sender} combined-media-part`}>
                            {renderMediaContent(message, message.sender)}
                          </div>
                          <div className={`message-bubble ${message.sender} combined-text-part`}>
                            {message.text.split('\n').map((line, index, arr) => (
                              <React.Fragment key={index}>
                                {line}
                                {index < arr.length - 1 && <br />}
                              </React.Fragment>
                            ))}
                          </div>
                          <div className={`message-time ${message.sender}`}>
                            {message.timestamp}
                          </div>
                        </React.Fragment>
                      ) : message.mediaPath && !message.text ? (
                        <React.Fragment>
                          <div className={`message-media ${message.sender}`}>
                            {renderMediaContent(message, message.sender)}
                          </div>
                          <div className={`message-time ${message.sender}`}>
                            {message.timestamp}
                          </div>
                        </React.Fragment>
                      ) : (
                        <React.Fragment>
                          <div className={`message-bubble ${message.sender}`}>
                            {message.text}
                          </div>
                          <div className={`message-time ${message.sender}`}>
                            {message.timestamp}
                          </div>
                        </React.Fragment>
                      )}
                    </div>
                  ))}
                </div>

                <div className="chat-input">
                  <div className="icon-wrapper" onClick={onAttachmentClick} style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}>
                    <IconButtonUI icon="icon-paperclip" disabled={isUploading} />
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      ref={fileInputRef}
                      onChange={onFileSelect}
                      accept="*"
                      disabled={isUploading}
                    />
                  </div>
                  
                  {selectedFile && (
                    <div className="selected-file">
                      {isUploading ? (
                        <span className="file-uploading">Uploading {selectedFile.name}...</span>
                      ) : (
                        <span className="file-selected">{selectedFile.name}</span>
                      )}
                    </div>
                  )}
                  
                  {uploadError && (
                    <div className="upload-error">{uploadError}</div>
                  )}
                  
                  <input 
                    className="input-field" 
                    placeholder="Type Message" 
                    value={messageText}
                    onChange={onMessageChange}
                    disabled={isUploading}
                  />
                  
                  <div
                    className={`icon-wrapper send-icon ${(isUploading || (!messageText && !selectedFile)) ? 'disabled' : ''}`}
                    onClick={!isUploading && (messageText || selectedFile) ? onSendMessage : undefined}
                  >
                    <IconButtonUI icon="icon-reply" disabled={isUploading || (!messageText && !selectedFile)} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </SuperUserAccountPageUI>
  )
}

export default WhatsAppChatPageUI
